import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, X, Clock, DollarSign, User, Briefcase, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OfferView = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const user = auth.getUser();
  
  const [loading, setLoading] = useState(true);
  const [offerData, setOfferData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchOffer();
  }, [offerId]);

  const fetchOffer = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/offers/${offerId}`);
      setOfferData(response.data);
    } catch (error) {
      console.error('Error fetching offer:', error);
      toast.error('Kunne ikke laste tilbud');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (processing) return;
    
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/marketplace/offers/${offerId}/accept`);
      
      toast.success(
        <div className="space-y-2">
          <p className="font-bold">🎉 Tilbud akseptert!</p>
          <p>Du er nå oppgradert til Pro-planen.</p>
        </div>,
        { duration: 5000 }
      );
      
      // Navigate to case detail after acceptance
      setTimeout(() => {
        navigate(`/case/${offerData.case.id}`);
      }, 2000);
      
    } catch (error) {
      console.error('Error accepting offer:', error);
      toast.error('Kunne ikke akseptere tilbud');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (processing || !declineReason.trim()) {
      toast.error('Vennligst oppgi en grunn');
      return;
    }
    
    setProcessing(true);
    try {
      await axios.post(`${API}/marketplace/offers/${offerId}/decline`, null, {
        params: { reason: declineReason }
      });
      
      toast.success('Tilbud avslått');
      
      // Navigate back after declining
      setTimeout(() => {
        navigate(`/case/${offerData.case.id}`);
      }, 1500);
      
    } catch (error) {
      console.error('Error declining offer:', error);
      toast.error('Kunne ikke avslå tilbud');
    } finally {
      setProcessing(false);
      setShowDeclineModal(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F2EE] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="text-slate-600 mt-4">Laster tilbud...</p>
        </div>
      </div>
    );
  }

  if (!offerData) {
    return (
      <div className="min-h-screen bg-[#F4F2EE] flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600">Tilbud ikke funnet</p>
        </div>
      </div>
    );
  }

  const { offer, case: caseData, lawyer } = offerData;

  // Check if already processed
  const isProcessed = offer.status !== 'PENDING';

  return (
    <div className="min-h-screen bg-[#F4F2EE]">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate(`/case/${caseData.id}`)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Tilbake til sak
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Status Banner */}
        {isProcessed && (
          <div className={`mb-6 p-4 rounded-xl border-2 ${
            offer.status === 'ACCEPTED' 
              ? 'bg-green-50 border-green-200' 
              : 'bg-slate-50 border-slate-200'
          }`}>
            <div className="flex items-center gap-3">
              {offer.status === 'ACCEPTED' ? (
                <>
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-bold text-green-900">Tilbud akseptert</p>
                    <p className="text-sm text-green-700">Du er oppgradert til Pro-planen. Advokaten jobber nå med saken din.</p>
                  </div>
                </>
              ) : (
                <>
                  <XCircle className="w-6 h-6 text-slate-600" />
                  <div>
                    <p className="font-bold text-slate-900">Tilbud avslått</p>
                    <p className="text-sm text-slate-600">Dette tilbudet er ikke lenger aktivt.</p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-br from-purple-600 to-purple-700 text-white p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold mb-2">Tilbud fra advokat</h1>
                <p className="text-purple-100 text-lg">{caseData.title}</p>
              </div>
            </div>
          </div>

          {/* Lawyer Info */}
          <div className="p-6 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{lawyer.name}</p>
                <p className="text-sm text-slate-600">{lawyer.email}</p>
              </div>
            </div>
          </div>

          {/* Offer Details */}
          <div className="p-8 space-y-6">
            {/* Personal Message */}
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Personlig melding fra advokat</h3>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-slate-700 whitespace-pre-wrap">{offer.personal_message}</p>
              </div>
            </div>

            {/* Time Estimate */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-purple-900">Estimert arbeidstid</span>
                </div>
                <p className="text-3xl font-bold text-purple-600">
                  {offer.hours_min}-{offer.hours_max} timer
                </p>
                <p className="text-sm text-purple-700 mt-1">
                  {offer.hourly_rate} kr per time
                </p>
              </div>

              <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-900">Estimert kostnad</span>
                </div>
                <p className="text-3xl font-bold text-green-600">
                  {offer.total_min.toLocaleString('no-NO')}-{offer.total_max.toLocaleString('no-NO')} kr
                </p>
                <p className="text-sm text-green-700 mt-1">
                  Faktureres etter faktisk arbeidstid
                </p>
              </div>
            </div>

            {/* Includes */}
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Inkludert i tilbudet</h3>
              <div className="space-y-2">
                {offer.includes.map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pro Plan Info */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
              <h3 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Inkludert: Gratis oppgradering til Pro-plan
              </h3>
              <ul className="space-y-2 text-sm text-purple-800">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-600"></span>
                  Full tilgang til Lexy dashboard
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-600"></span>
                  Direktemelding med advokat
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-600"></span>
                  Timeregistrering og dokumentsporing
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-600"></span>
                  Prioritert support
                </li>
              </ul>
            </div>

            {/* Important Note */}
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <p className="text-sm text-blue-900">
                <span className="font-semibold">Viktig:</span> Du betaler kun for faktisk arbeidstid. 
                Advokaten sender deg timeregistrering løpende, og du får en faktura når arbeidet er ferdig.
              </p>
            </div>

            {/* Action Buttons */}
            {!isProcessed && (
              <div className="flex gap-4 pt-4">
                <Button
                  onClick={handleAccept}
                  disabled={processing}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold"
                >
                  {processing ? (
                    'Behandler...'
                  ) : (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      Aksepter tilbud
                    </>
                  )}
                </Button>

                <Button
                  onClick={() => setShowDeclineModal(true)}
                  disabled={processing}
                  variant="outline"
                  className="px-8 py-6 text-lg font-semibold border-2"
                >
                  <X className="w-5 h-5 mr-2" />
                  Avslå
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Decline Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Avslå tilbud</h3>
            <p className="text-slate-600 mb-4">
              Vennligst oppgi en kort begrunnelse for hvorfor du avslår tilbudet. 
              Dette hjelper advokaten med å justere tilbudet eller finne en bedre løsning.
            </p>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="F.eks. 'For dyrt', 'Trenger mer tid til å tenke', 'Fant annen løsning'..."
              className="w-full p-3 border border-slate-300 rounded-lg min-h-[100px] mb-4"
              autoFocus
            />
            <div className="flex gap-3">
              <Button
                onClick={handleDecline}
                disabled={!declineReason.trim() || processing}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {processing ? 'Behandler...' : 'Bekreft avslag'}
              </Button>
              <Button
                onClick={() => setShowDeclineModal(false)}
                variant="outline"
                className="px-6"
              >
                Avbryt
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferView;
